import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import * as firestore from "@/lib/firestore";
import type { DocumentSnapshot } from "firebase/firestore";
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy,
  writeBatch
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { queryClient } from "@/lib/queryClient";
import type {
  InsertVehicle,
  InsertAppointment,
  InsertWash,
  InsertService,
  InsertEmployeeProfile,
  InsertEmployeePayment,
  InsertCoupon,
  InsertCouponUsageLog,
  InsertExpense,
  InsertFixedExpense,
  InsertReview,
  InsertCorporateQuote,
  CorporateQuote,
  InsertCorporateCompany,
  CorporateCompany,
  InsertPayment,
  InsertCustomerLoyalty,
  LoyaltyRedemption,
  InsertSiteSettings,
  InsertSystemSettings,
  InsertNotificationTemplate,
  NotificationSound as NotificationSoundType,
  InsertNotificationSound,
  TemplateVersion,
  InsertGift,
  InsertFeedback,
  Feedback,
  InsertInventoryItem,
  InventoryItem,
  InsertStockMovement,
  StockMovement,
  Testimonial,
  InsertTestimonial,
  ContactSettings,
  InsertContactSettings,
  GalleryItem,
  InsertGalleryItem,
  GalleryPageSettings,
  InsertGalleryPageSettings,
  GalleryCategory_Type,
  InsertGalleryCategory,
  BlogPost,
  InsertBlogPost,
  Popup,
  InsertPopup,
  InsertCorporatePricing,
  CorporatePricing,
  InsertCorporateInvoice,
  CorporateInvoice,
  DeploymentSettings,
  InsertDeploymentSettings,
  UserNotification,
  InsertUserNotification,
  CustomerAnalytics,
  InsertCustomerAnalytics,
  CorporateAgreement,
  InsertCorporateAgreement,
  VehicleAnalytics,
  InsertVehicleAnalytics,
  Wash,
  SubscriptionPackage,
  InsertSubscriptionPackage,
  CustomerSubscription,
  InsertCustomerSubscription,
  SubscriptionUsage,
  InsertSubscriptionUsage,
  CorporatePayment,
  InsertCorporatePayment,
  CorporateDebt,
  InsertCorporateDebt,
} from "@shared/schema";

// ============================================================================
// QUERY KEYS
// ============================================================================

export const QUERY_KEYS = {
  user: (userId: string) => ["user", userId],
  vehicles: (userId: string) => ["vehicles", userId],
  vehicle: (vehicleId: string) => ["vehicle", vehicleId],
  services: () => ["services"],
  activeServices: () => ["services", "active"],
  appointments: (userId: string) => ["appointments", userId],
  appointment: (appointmentId: string) => ["appointment", appointmentId],
  appointmentByCode: (code: string) => ["appointment", "code", code],
  appointmentsByDate: (startDate: string, endDate: string) => ["appointments", "range", startDate, endDate],
  washes: (userId: string) => ["washes", userId],
  wash: (washId: string) => ["wash", washId],
  washesByStatus: (status: string) => ["washes", "status", status],
  todayWashes: () => ["washes", "today"],
  allWashes: () => ["washes", "all"],
  employees: () => ["employees"],
  activeEmployees: () => ["employees", "active"],
  employeePayments: (employeeId?: string) => employeeId ? ["employeePayments", employeeId] : ["employeePayments"],
  employeePaymentsByMonth: (month: string) => ["employeePayments", "month", month],
  coupons: () => ["coupons"],
  coupon: (code: string) => ["coupon", code],
  expenses: () => ["expenses"],
  expensesByDate: (startDate: string, endDate: string) => ["expenses", "range", startDate, endDate],
  fixedExpenses: () => ["fixedExpenses"],
  reviews: () => ["reviews"],
  publicReviews: () => ["reviews", "public"],
  pendingReviews: () => ["reviews", "pending"],
  payments: () => ["payments"],
  paymentsByDate: (startDate: string, endDate: string) => ["payments", "range", startDate, endDate],
  loyalty: (customerId: string) => ["loyalty", customerId],
  allLoyalty: () => ["loyalty", "all"],
  redemptions: () => ["redemptions", "all"],
  customerRedemptions: (customerId: string) => ["redemptions", customerId],
  rewardCoupons: (customerId: string) => ["rewardCoupons", customerId],
  allRewardCoupons: () => ["rewardCoupons", "all"],
  rewardCouponByCode: (code: string) => ["rewardCoupon", "code", code],
  siteSettings: () => ["settings"],
  systemSettings: () => ["systemSettings"],
  notificationTemplates: () => ["notificationTemplates"],
  notificationSounds: () => ["notificationSounds"],
  activeNotificationSounds: () => ["notificationSounds", "active"],
  templateVersions: (templateId: string) => ["templateVersions", templateId],
  gifts: () => ["gifts"],
  activeGifts: () => ["gifts", "active"],
  feedback: () => ["feedback"],
  smsTemplates: () => ["smsTemplates"],
  smsLogs: () => ["smsLogs"],
  smsSettings: () => ["smsSettings"],
  smsBalance: () => ["smsBalance"],
  campaignSmsTemplates: () => ["campaignSmsTemplates"],
  campaignSmsTemplate: (templateId: string) => ["campaignSmsTemplate", templateId],
  inventory: () => ["inventory"],
  inventoryItem: (itemId: string) => ["inventory", itemId],
  criticalStock: () => ["inventory", "critical"],
  stockMovements: (itemId?: string) => itemId ? ["stockMovements", itemId] : ["stockMovements"],
  popups: () => ["popups"],
  popupsByPage: (pageId: string) => ["popups", "page", pageId],
  popup: (popupId: string) => ["popup", popupId],
  emailTemplates: () => ["emailTemplates"],
  emailTemplate: (templateId: string) => ["emailTemplate", templateId],
  emailLogs: () => ["emailLogs"],
  emailSettings: () => ["emailSettings"],
  corporatePricing: (companyId: string) => ["corporatePricing", companyId],
  allCorporatePricing: () => ["corporatePricing", "all"],
  corporateInvoices: (companyId: string) => ["corporateInvoices", companyId],
  allCorporateInvoices: () => ["corporateInvoices", "all"],
  companyWashes: (companyId: string) => ["washes", "company", companyId],
  companyPayments: (companyId: string) => ["payments", "company", companyId],
  companyVehicles: (companyId: string) => ["vehicles", "company", companyId],
  vehicleByPlate: (plate: string) => ["vehicle", "plate", plate],
  vehicleAppointments: (vehicleId: string) => ["vehicle", vehicleId, "appointments"],
  deploymentSettings: () => ["deploymentSettings"],
  userNotifications: (userId: string) => ["userNotifications", userId],
  userNotification: (userId: string, notificationId: string) => ["userNotification", userId, notificationId],
  customerAnalytics: (customerId: string) => ["customerAnalytics", customerId],
  allCustomerAnalytics: () => ["customerAnalytics", "all"],
  topCustomersBySpending: (limit: number) => ["customerAnalytics", "topSpending", limit],
  topCustomersByVisits: (limit: number) => ["customerAnalytics", "topVisits", limit],
  vipCustomers: () => ["customerAnalytics", "vip"],
  corporateAgreement: (agreementId: string) => ["corporateAgreement", agreementId],
  corporateAgreementsByCompany: (companyId: string) => ["corporateAgreements", "company", companyId],
  activeAgreementsByCompany: (companyId: string) => ["corporateAgreements", "company", companyId, "active"],
  allCorporateAgreements: () => ["corporateAgreements", "all"],
  expiringAgreements: (days: number) => ["corporateAgreements", "expiring", days],
  vehicleAnalytics: (vehicleId: string) => ["vehicleAnalytics", vehicleId],
  allVehicleAnalytics: () => ["vehicleAnalytics", "all"],
  topVehiclesByWashCount: (limit: number) => ["vehicleAnalytics", "topWashes", limit],
  topVehiclesByRevenue: (limit: number) => ["vehicleAnalytics", "topRevenue", limit],
  customerWashHistory: (customerId: string) => ["washes", "customer", customerId],
  vehicleWashHistory: (vehicleId: string) => ["washes", "vehicle", vehicleId],
  
  // Subscription Package keys
  subscriptionPackages: () => ["subscriptionPackages"],
  activeSubscriptionPackages: () => ["subscriptionPackages", "active"],
  subscriptionPackage: (packageId: string) => ["subscriptionPackage", packageId],
  
  // Customer Subscription keys
  customerSubscriptions: (userId: string) => ["customerSubscriptions", userId],
  customerSubscription: (subscriptionId: string) => ["customerSubscription", subscriptionId],
  activeCustomerSubscriptions: (userId: string) => ["customerSubscriptions", userId, "active"],
  companySubscriptions: (companyId: string) => ["customerSubscriptions", "company", companyId],
  allCustomerSubscriptions: () => ["customerSubscriptions", "all"],
  expiringSubscriptions: (days: number) => ["customerSubscriptions", "expiring", days],
  
  // Subscription Usage keys
  subscriptionUsage: (subscriptionId: string) => ["subscriptionUsage", subscriptionId],
  allSubscriptionUsage: () => ["subscriptionUsage", "all"],
  
  // Budget keys
  currentBudget: (corporateCustomerId: string, month: number, year: number) => ["budget", corporateCustomerId, month, year],
  corporateBudgets: (corporateCustomerId: string) => ["budgets", corporateCustomerId],
  
  // Subscription Analytics keys
  subscriptionAnalytics: () => ["subscriptionAnalytics"],
  wastedQuotas: () => ["wastedQuotas"],
};

// ============================================================================
// USER HOOKS
// ============================================================================

export function useUser(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.user(userId) : ["user", "null"],
    queryFn: () => (userId ? firestore.getUser(userId) : null),
    enabled: !!userId,
  });
}

export function useAllUsers() {
  return useQuery({
    queryKey: ["users", "all"],
    queryFn: () => firestore.getAllUsers(),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useUsersPaginated(pageSize: number = 50) {
  return useInfiniteQuery({
    queryKey: ["users", "paginated"],
    queryFn: async ({ pageParam }) => {
      return firestore.getUsersPaginated(pageSize, pageParam as DocumentSnapshot | undefined);
    },
    initialPageParam: undefined as DocumentSnapshot | undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.lastDoc : undefined,
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: Partial<any> }) =>
      firestore.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useReviews() {
  return useQuery({
    queryKey: ["reviews", "all"],
    queryFn: () => firestore.getAllReviews(),
  });
}

export function useApproveReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reviewId: string) => firestore.approveReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reviewId: string) => firestore.deleteReview(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
  });
}

// ============================================================================
// VEHICLE HOOKS
// ============================================================================

export function useVehicles(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.vehicles(userId) : ["vehicles", "null"],
    queryFn: () => (userId ? firestore.getUserVehicles(userId) : []),
    enabled: !!userId,
  });
}

export function useVehicle(vehicleId: string | undefined) {
  return useQuery({
    queryKey: vehicleId ? QUERY_KEYS.vehicle(vehicleId) : ["vehicle", "null"],
    queryFn: () => (vehicleId ? firestore.getVehicle(vehicleId) : null),
    enabled: !!vehicleId,
  });
}

export function useAllVehicles() {
  return useQuery({
    queryKey: ["vehicles", "all"],
    queryFn: () => firestore.getAllVehicles(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertVehicle) => firestore.createVehicle(data),
    onSuccess: (_, variables) => {
      if (variables.userId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.vehicles(variables.userId) });
      }
      queryClient.invalidateQueries({ queryKey: ["vehicles", "all"] });
    },
  });
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: Partial<InsertVehicle> }) =>
      firestore.updateVehicle(vehicleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.vehicle(variables.vehicleId) });
    },
  });
}

export function useDeleteVehicle() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (vehicleId: string) => firestore.deleteVehicle(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    },
  });
}

export function useCompanyVehicles(companyId: string | undefined) {
  return useQuery({
    queryKey: companyId ? QUERY_KEYS.companyVehicles(companyId) : ["vehicles", "company", "null"],
    queryFn: () => (companyId ? firestore.getCompanyVehicles(companyId) : []),
    enabled: !!companyId,
  });
}

export function useVehicleByPlate(plate: string | undefined) {
  return useQuery({
    queryKey: plate ? QUERY_KEYS.vehicleByPlate(plate) : ["vehicle", "plate", "null"],
    queryFn: () => (plate ? firestore.getVehicleByPlateOnly(plate) : null),
    enabled: !!plate && plate.length > 0,
  });
}

export function useAddVehicleToCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertVehicle & { companyId: string }) => 
      firestore.createVehicle(data),
    onSuccess: (_, variables) => {
      if (variables.companyId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companyVehicles(variables.companyId) });
      }
      queryClient.invalidateQueries({ queryKey: ["vehicles", "all"] });
    },
  });
}

export function useRemoveVehicleFromCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicleId, companyId }: { vehicleId: string; companyId: string }) =>
      firestore.updateVehicle(vehicleId, { companyId: null }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companyVehicles(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.vehicle(variables.vehicleId) });
      queryClient.invalidateQueries({ queryKey: ["vehicles", "all"] });
    },
  });
}

// ============================================================================
// SERVICE HOOKS
// ============================================================================

export function useServices() {
  return useQuery({
    queryKey: QUERY_KEYS.services(),
    queryFn: () => firestore.getAllServices(),
  });
}

export function useActiveServices() {
  return useQuery({
    queryKey: QUERY_KEYS.activeServices(),
    queryFn: () => firestore.getActiveServices(),
  });
}

export function useService(serviceId: string | undefined) {
  return useQuery({
    queryKey: serviceId ? ["service", serviceId] : ["service", "null"],
    queryFn: () => (serviceId ? firestore.getService(serviceId) : null),
    enabled: !!serviceId,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertService) => firestore.createService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeServices() });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: Partial<InsertService> }) =>
      firestore.updateService(serviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeServices() });
    },
  });
}

// ============================================================================
// APPOINTMENT HOOKS
// ============================================================================

export function useAppointments(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.appointments(userId) : ["appointments", "null"],
    queryFn: () => (userId ? firestore.getUserAppointments(userId) : []),
    enabled: !!userId,
  });
}

export function useAppointment(appointmentId: string | undefined) {
  return useQuery({
    queryKey: appointmentId ? QUERY_KEYS.appointment(appointmentId) : ["appointment", "null"],
    queryFn: () => (appointmentId ? firestore.getAppointment(appointmentId) : null),
    enabled: !!appointmentId,
  });
}

export function useAppointmentByCode(code: string | undefined) {
  return useQuery({
    queryKey: code ? QUERY_KEYS.appointmentByCode(code) : ["appointment", "code", "null"],
    queryFn: () => (code ? firestore.getAppointmentByCode(code) : null),
    enabled: !!code && code.length === 9,
  });
}

export function useAppointmentsByDateRange(startDate: string, endDate: string) {
  return useQuery({
    queryKey: QUERY_KEYS.appointmentsByDate(startDate, endDate),
    queryFn: async () => {
      console.log("📅 [useAppointmentsByDateRange] Query started:", { startDate, endDate });
      try {
        const result = await firestore.getAppointmentsByDateRange(startDate, endDate);
        console.log(`✅ [useAppointmentsByDateRange] Query successful! Found ${result.length} appointments`);
        return result;
      } catch (error: any) {
        if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
          console.log("ℹ️ [useAppointmentsByDateRange] Permission denied - returning empty array (normal for non-staff users)");
          return [];
        }
        console.error("❌ [useAppointmentsByDateRange] Query failed:", error);
        throw error;
      }
    },
  });
}

export function useTodayPendingAppointments() {
  return useQuery({
    queryKey: ["appointments", "today-pending"],
    queryFn: () => firestore.getTodayPendingAppointments(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data refreshes on mutation
    gcTime: 15 * 60 * 1000,
    // Removed refetchInterval - rely on mutation invalidation instead
  });
}

export function useAllPendingAppointments() {
  return useQuery({
    queryKey: ["appointments", "all-pending"],
    queryFn: () => firestore.getAllPendingAppointments(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useAllConfirmedAppointments() {
  return useQuery({
    queryKey: ["appointments", "all-confirmed"],
    queryFn: () => firestore.getAllConfirmedAppointments(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useAllAppointments() {
  return useQuery({
    queryKey: ["appointments", "all"],
    queryFn: () => firestore.getAllAppointments(),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertAppointment) => firestore.createAppointment(data),
    onSuccess: (_, variables) => {
      if (variables.customerId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.appointments(variables.customerId) });
      }
      queryClient.invalidateQueries({ queryKey: ["appointments", "range"] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ appointmentId, data }: { appointmentId: string; data: Partial<InsertAppointment> }) =>
      firestore.updateAppointment(appointmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (appointmentId: string) => firestore.deleteAppointment(appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

export function useCancelAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (appointmentId: string) => firestore.cancelAppointment(appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    },
  });
}

// ============================================================================
// WASH HOOKS
// ============================================================================

export function useWashes(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.washes(userId) : ["washes", "null"],
    queryFn: () => (userId ? firestore.getUserWashes(userId) : []),
    enabled: !!userId,
  });
}

export function useWash(washId: string | undefined) {
  return useQuery({
    queryKey: washId ? QUERY_KEYS.wash(washId) : ["wash", "null"],
    queryFn: () => (washId ? firestore.getWash(washId) : null),
    enabled: !!washId,
  });
}

export function useWashesByStatus(status: string) {
  return useQuery({
    queryKey: QUERY_KEYS.washesByStatus(status),
    queryFn: () => firestore.getWashesByStatus(status),
  });
}

export function useTodayWashes() {
  return useQuery({
    queryKey: QUERY_KEYS.todayWashes(),
    queryFn: () => firestore.getTodayWashes(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data refreshes on mutation
    gcTime: 15 * 60 * 1000,
  });
}

export function useAllWashes() {
  return useQuery({
    queryKey: QUERY_KEYS.allWashes(),
    queryFn: () => firestore.getAllWashes(),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useWashesByDateRange(startDate: string, endDate: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["washes", "dateRange", startDate, endDate],
    queryFn: () => firestore.getWashesByDateRange(startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
  });
}

export function useWashesByDate(dateStr: string) {
  return useQuery({
    queryKey: ["washes", "date", dateStr],
    queryFn: () => firestore.getWashesByDate(dateStr),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useEmployeeWashesByDate(employeeId: string | undefined, dateStr: string) {
  return useQuery({
    queryKey: ["washes", "employee", employeeId, "date", dateStr],
    queryFn: () => firestore.getEmployeeWashesByDate(employeeId!, dateStr),
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useEmployeeWashes(employeeId: string | undefined) {
  return useQuery({
    queryKey: ["washes", "employee", employeeId],
    queryFn: () => firestore.getEmployeeWashes(employeeId!),
    enabled: !!employeeId,
  });
}

export function useUserWashes(userId: string | undefined) {
  return useQuery({
    queryKey: ["washes", "user", userId],
    queryFn: () => firestore.getUserWashes(userId!),
    enabled: !!userId,
  });
}

export function useCreateWash() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertWash) => firestore.createWash(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
    },
  });
}

export function useUpdateWash() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ washId, data }: { washId: string; data: Partial<InsertWash> }) =>
      firestore.updateWash(washId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
    },
  });
}

export function useDeleteWash() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (washId: string) => firestore.deleteWash(washId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allWashes() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments() });
    },
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (paymentId: string) => firestore.deletePayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments() });
      queryClient.invalidateQueries({ queryKey: ["washes"] });
    },
  });
}

export function useCreateWashFromAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      appointmentId, 
      washData, 
      employeeId 
    }: { 
      appointmentId: string; 
      washData: Omit<InsertWash, 'appointmentId' | 'createdAt'>; 
      employeeId: string;
    }) => firestore.createWashFromAppointment(appointmentId, washData, employeeId),
    onSuccess: (_data, variables) => {
      // Invalidate all wash and appointment queries
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.appointment(variables.appointmentId) });
    },
  });
}

export function useCompleteWashWithLoyalty() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      washId, 
      customerId, 
      finalAmount 
    }: { 
      washId: string; 
      customerId: string | null; 
      finalAmount: number;
    }) => firestore.completeWashWithLoyalty(washId, customerId, finalAmount),
    onMutate: async ({ washId }) => {
      await queryClient.cancelQueries({ queryKey: ["washes"] });
      const previousWashes = queryClient.getQueryData(QUERY_KEYS.todayWashes());
      
      queryClient.setQueryData(QUERY_KEYS.todayWashes(), (old: any) => {
        if (!old) return old;
        return old.map((wash: any) => 
          wash.id === washId 
            ? { ...wash, status: 'completed', completedAt: Date.now() }
            : wash
        );
      });
      
      return { previousWashes };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousWashes) {
        queryClient.setQueryData(QUERY_KEYS.todayWashes(), context.previousWashes);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
    },
  });
}

// ============================================================================
// EMPLOYEE HOOKS
// ============================================================================

export function useEmployees() {
  return useQuery({
    queryKey: QUERY_KEYS.employees(),
    queryFn: () => firestore.getAllEmployees(),
  });
}

export function useActiveEmployees() {
  return useQuery({
    queryKey: QUERY_KEYS.activeEmployees(),
    queryFn: () => firestore.getActiveEmployees(),
  });
}

export function useEmployeeUsers() {
  return useQuery({
    queryKey: ["users", "employees"],
    queryFn: () => firestore.getEmployeeUsers(),
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertEmployeeProfile) => firestore.createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeEmployees() });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: Partial<InsertEmployeeProfile> }) =>
      firestore.updateEmployee(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeEmployees() });
    },
  });
}

// ============================================================================
// EMPLOYEE PAYMENT HOOKS
// ============================================================================

export function useEmployeePayments(employeeId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.employeePayments(employeeId),
    queryFn: () => firestore.getEmployeePayments(employeeId),
  });
}

export function useEmployeePaymentsByMonth(month: string) {
  return useQuery({
    queryKey: QUERY_KEYS.employeePaymentsByMonth(month),
    queryFn: () => firestore.getEmployeePaymentsByMonth(month),
  });
}

export function useCreateEmployeePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertEmployeePayment) => firestore.createEmployeePayment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employeePayments"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses() });
    },
  });
}

export function useUpdateEmployeePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: Partial<InsertEmployeePayment> }) =>
      firestore.updateEmployeePayment(paymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employeePayments"] });
    },
  });
}

export function useDeleteEmployeePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (paymentId: string) => firestore.deleteEmployeePayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employeePayments"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses() });
    },
  });
}

// ============================================================================
// COUPON HOOKS
// ============================================================================

export function useCoupons() {
  return useQuery({
    queryKey: QUERY_KEYS.coupons(),
    queryFn: () => firestore.getAllCoupons(),
  });
}

export function useCoupon(code: string | undefined) {
  return useQuery({
    queryKey: code ? QUERY_KEYS.coupon(code) : ["coupon", "null"],
    queryFn: () => (code ? firestore.getCouponByCode(code) : null),
    enabled: !!code,
  });
}

export function useCreateCoupon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCoupon) => firestore.createCoupon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.coupons() });
    },
  });
}

export function useUpdateCoupon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ couponId, data }: { couponId: string; data: Partial<InsertCoupon> }) =>
      firestore.updateCoupon(couponId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.coupons() });
    },
  });
}

export function useCouponUsageLogs(couponId: string | undefined) {
  return useQuery({
    queryKey: couponId ? ['couponUsageLogs', couponId] : ['couponUsageLogs', 'null'],
    queryFn: () => (couponId ? firestore.getCouponUsageLogs(couponId) : []),
    enabled: !!couponId,
  });
}

export function useCreateCouponUsageLog() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCouponUsageLog) => firestore.createCouponUsageLog(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['couponUsageLogs', variables.couponId] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.coupons() });
    },
  });
}

export function useIncrementCouponUsage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (couponId: string) => firestore.incrementCouponUsage(couponId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.coupons() });
    },
  });
}

// ============================================================================
// EXPENSE HOOKS
// ============================================================================

export function useExpenses() {
  return useQuery({
    queryKey: QUERY_KEYS.expenses(),
    queryFn: () => firestore.getAllExpenses(),
  });
}

export function useExpensesByDateRange(startDate: string, endDate: string) {
  return useQuery({
    queryKey: QUERY_KEYS.expensesByDate(startDate, endDate),
    queryFn: () => firestore.getExpensesByDateRange(startDate, endDate),
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertExpense) => firestore.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses() });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (expenseId: string) => firestore.deleteExpense(expenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenses() });
    },
  });
}

// ============================================================================
// FIXED EXPENSE HOOKS (Sabit Giderler)
// ============================================================================

export function useFixedExpenses() {
  return useQuery({
    queryKey: QUERY_KEYS.fixedExpenses(),
    queryFn: () => firestore.getAllFixedExpenses(),
  });
}

export function useCreateFixedExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertFixedExpense) => firestore.createFixedExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.fixedExpenses() });
    },
  });
}

export function useDeleteFixedExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (fixedExpenseId: string) => firestore.deleteFixedExpense(fixedExpenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.fixedExpenses() });
    },
  });
}

// ============================================================================
// REVIEW HOOKS
// ============================================================================

export function usePublicReviews() {
  return useQuery({
    queryKey: QUERY_KEYS.publicReviews(),
    queryFn: () => firestore.getPublicReviews(),
  });
}

export function usePendingReviews() {
  return useQuery({
    queryKey: QUERY_KEYS.pendingReviews(),
    queryFn: () => firestore.getPendingReviews(),
  });
}

export function useCreateReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertReview) => firestore.createReview(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reviews() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingReviews() });
    },
  });
}

export function useUpdateReview() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reviewId, data }: { reviewId: string; data: Partial<InsertReview> }) =>
      firestore.updateReview(reviewId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.reviews() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.publicReviews() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingReviews() });
    },
  });
}

// ============================================================================
// PHOTO CLEANUP HOOKS (KVKK Compliance)
// ============================================================================

export function useCleanupOldPhotos() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => firestore.cleanupOldPhotos(),
    onSuccess: () => {
      // Invalidate all wash-related queries after cleanup
      queryClient.invalidateQueries({ queryKey: ['washes'] }); // All washes queries
      queryClient.invalidateQueries({ queryKey: ['todayWashes'] }); // Admin dashboard
    },
  });
}

// ============================================================================
// CORPORATE COMPANY HOOKS
// ============================================================================

export function useCreateCorporateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCorporateCompany) => firestore.createCorporateCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporateCompanies'] });
    },
  });
}

export function useCorporateCompanies() {
  return useQuery({
    queryKey: ['corporateCompanies'],
    queryFn: () => firestore.getAllCorporateCompanies(),
  });
}

export function useCorporateCompanyById(companyId: string | undefined) {
  return useQuery({
    queryKey: ['corporateCompany', companyId],
    queryFn: () => companyId ? firestore.getCorporateCompanyById(companyId) : null,
    enabled: !!companyId,
  });
}

export function useUpdateCorporateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: Partial<CorporateCompany> }) =>
      firestore.updateCorporateCompany(companyId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporateCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['corporateCompany', variables.companyId] });
    },
  });
}

export function useDeleteCorporateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (companyId: string) => firestore.deleteCorporateCompany(companyId),
    onSuccess: (_data, companyId) => {
      queryClient.invalidateQueries({ queryKey: ['corporateCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['corporateCompany', companyId] });
    },
  });
}

// ============================================================================
// CORPORATE PAYMENT (CARİ ÖDEME) HOOKS
// ============================================================================

export function useCorporatePayments(companyId?: string) {
  return useQuery({
    queryKey: ['corporatePayments', companyId],
    queryFn: () => firestore.getCorporatePayments(companyId),
  });
}

export function useCreateCorporatePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCorporatePayment) => firestore.createCorporatePayment(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporatePayments'] });
      queryClient.invalidateQueries({ queryKey: ['corporatePayments', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['corporateDebts'] });
      queryClient.invalidateQueries({ queryKey: ['corporateBalance'] });
    },
  });
}

export function useDeleteCorporatePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (paymentId: string) => firestore.deleteCorporatePayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporatePayments'] });
      queryClient.invalidateQueries({ queryKey: ['corporateDebts'] });
      queryClient.invalidateQueries({ queryKey: ['corporateBalance'] });
    },
  });
}

// ============================================================================
// CORPORATE DEBT (CARİ BORÇ) HOOKS
// ============================================================================

export function useCorporateDebts(companyId?: string) {
  return useQuery({
    queryKey: ['corporateDebts', companyId],
    queryFn: () => firestore.getCorporateDebts(companyId),
  });
}

export function useUnpaidCorporateDebts() {
  return useQuery({
    queryKey: ['corporateDebts', 'unpaid'],
    queryFn: () => firestore.getUnpaidCorporateDebts(),
  });
}

export function useCorporateBalanceSummary() {
  return useQuery({
    queryKey: ['corporateBalance', 'summary'],
    queryFn: () => firestore.getCorporateBalanceSummary(),
  });
}

export function useMarkDebtsAsPaid() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ debtIds, paymentId }: { debtIds: string[]; paymentId: string }) => 
      firestore.markDebtsAsPaid(debtIds, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporateDebts'] });
      queryClient.invalidateQueries({ queryKey: ['corporateBalance'] });
    },
  });
}

// ============================================================================
// CORPORATE QUOTE HOOKS
// ============================================================================

export function useCreateCorporateQuote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCorporateQuote) => firestore.createCorporateQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporateQuotes'] });
    },
  });
}

export function useCorporateQuotes() {
  return useQuery({
    queryKey: ['corporateQuotes'],
    queryFn: () => firestore.getAllCorporateQuotes(),
  });
}

export function useCorporateQuoteById(quoteId: string | undefined) {
  return useQuery({
    queryKey: ['corporateQuote', quoteId],
    queryFn: () => quoteId ? firestore.getCorporateQuoteById(quoteId) : null,
    enabled: !!quoteId,
  });
}

export function useUpdateCorporateQuote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ quoteId, data }: { quoteId: string; data: Partial<CorporateQuote> }) =>
      firestore.updateCorporateQuote(quoteId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporateQuotes'] });
      queryClient.invalidateQueries({ queryKey: ['corporateQuote', variables.quoteId] });
    },
  });
}

export function useUpdateQuoteStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ quoteId, newStatus, updatedBy }: { quoteId: string; newStatus: string; updatedBy?: string }) =>
      firestore.updateQuoteStatus(quoteId, newStatus, updatedBy),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporateQuotes'] });
      queryClient.invalidateQueries({ queryKey: ['corporateQuote', variables.quoteId] });
    },
  });
}

export function useFinalizeQuote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ quoteId, updatedBy }: { quoteId: string; updatedBy: string }) =>
      firestore.finalizeQuoteWithDetails(quoteId, updatedBy),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['corporateQuotes'] });
      queryClient.invalidateQueries({ queryKey: ['corporateQuote', variables.quoteId] });
    },
  });
}

export function useDeleteCorporateQuote() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (quoteId: string) => firestore.deleteCorporateQuote(quoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporateQuotes'] });
    },
  });
}

// ============================================================================
// PAYMENT HOOKS
// ============================================================================

export function usePayments() {
  return useQuery({
    queryKey: QUERY_KEYS.payments(),
    queryFn: () => firestore.getAllPayments(),
  });
}

export function useCreatePayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertPayment) => firestore.createPayment(data),
    onMutate: async (newPayment) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.payments() });
      const previousPayments = queryClient.getQueryData(QUERY_KEYS.payments());
      
      queryClient.setQueryData(QUERY_KEYS.payments(), (old: any) => {
        if (!old) return [{ ...newPayment, id: 'temp-' + Date.now(), createdAt: Date.now() }];
        return [...old, { ...newPayment, id: 'temp-' + Date.now(), createdAt: Date.now() }];
      });
      
      return { previousPayments };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousPayments) {
        queryClient.setQueryData(QUERY_KEYS.payments(), context.previousPayments);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payments() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allLoyalty() });
      queryClient.invalidateQueries({ queryKey: ["washes"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todayWashes() });
    },
  });
}

export function usePaymentsByDateRange(startDate: string, endDate: string) {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  
  return useQuery({
    queryKey: QUERY_KEYS.paymentsByDate(startDate, endDate),
    queryFn: () => firestore.getPaymentsByDateRange(start, end),
    enabled: !!startDate && !!endDate,
  });
}

export function useDailyStats(dateStr: string) {
  return useQuery({
    queryKey: ["dailyStats", dateStr],
    queryFn: () => firestore.getDailyStats(dateStr),
    staleTime: 10 * 60 * 1000, // 10 minutes - stats don't change frequently
    gcTime: 30 * 60 * 1000,
  });
}

// ============================================================================
// CUSTOMER LOYALTY HOOKS
// ============================================================================

export function useCustomerLoyalty(customerId: string | undefined) {
  return useQuery({
    queryKey: customerId ? QUERY_KEYS.loyalty(customerId) : ["loyalty", "null"],
    queryFn: () => (customerId ? firestore.getCustomerLoyalty(customerId) : null),
    enabled: !!customerId,
  });
}

export function useAddLoyaltyPoints() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ customerId, amount }: { customerId: string; amount: number }) =>
      firestore.addLoyaltyPoints(customerId, amount),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.loyalty(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allLoyalty() });
    },
  });
}

export function useRedeemLoyaltyPoints() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ customerId, points, redeemedBy }: { customerId: string; points: number; redeemedBy: string }) =>
      firestore.redeemLoyaltyPoints(customerId, points, redeemedBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.loyalty(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allLoyalty() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.redemptions() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerRedemptions(variables.customerId) });
    },
  });
}

export function useAllLoyaltyRecords() {
  return useQuery({
    queryKey: QUERY_KEYS.allLoyalty(),
    queryFn: () => firestore.getAllLoyaltyRecords(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useInitializeLoyaltyForAll() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => firestore.initializeLoyaltyForAllCustomers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allLoyalty() });
    },
  });
}

export function useDeleteCustomerLoyalty() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (loyaltyId: string) => firestore.deleteCustomerLoyalty(loyaltyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allLoyalty() });
    },
  });
}

export function useAllRedemptions() {
  return useQuery({
    queryKey: QUERY_KEYS.redemptions(),
    queryFn: () => firestore.getAllRedemptions(),
  });
}

export function useCustomerRedemptions(customerId: string | undefined) {
  return useQuery({
    queryKey: customerId ? QUERY_KEYS.customerRedemptions(customerId) : ["redemptions", "null"],
    queryFn: () => (customerId ? firestore.getRedemptionsByCustomer(customerId) : []),
    enabled: !!customerId,
  });
}

// ============================================================================
// REWARD COUPON HOOKS (QR Kodlu Hediye Kuponları)
// ============================================================================

export function useCustomerRewardCoupons(customerId: string | undefined) {
  return useQuery({
    queryKey: customerId ? QUERY_KEYS.rewardCoupons(customerId) : ["rewardCoupons", "null"],
    queryFn: () => (customerId ? firestore.getCustomerRewardCoupons(customerId) : []),
    enabled: !!customerId,
  });
}

export function useActiveCustomerCoupons(customerId: string | undefined) {
  return useQuery({
    queryKey: customerId ? [...QUERY_KEYS.rewardCoupons(customerId), "active"] : ["rewardCoupons", "null"],
    queryFn: () => (customerId ? firestore.getActiveCustomerCoupons(customerId) : []),
    enabled: !!customerId,
  });
}

export function useRewardCouponByCode(code: string | undefined) {
  return useQuery({
    queryKey: code ? QUERY_KEYS.rewardCouponByCode(code) : ["rewardCoupon", "null"],
    queryFn: () => (code ? firestore.getRewardCouponByCode(code) : null),
    enabled: !!code,
  });
}

export function useRedeemRewardCoupon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ couponId, cashierId, washId }: { couponId: string; cashierId: string; washId?: string }) =>
      firestore.redeemRewardCoupon(couponId, cashierId, washId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rewardCoupons"] });
      queryClient.invalidateQueries({ queryKey: ["rewardCoupon"] });
    },
  });
}

// Alias for simpler usage
export function useRedeemCoupon() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (couponId: string) =>
      firestore.redeemRewardCoupon(couponId, "cashier", undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rewardCoupons"] });
      queryClient.invalidateQueries({ queryKey: ["rewardCoupon"] });
    },
  });
}

export function useAllRewardCoupons() {
  return useQuery({
    queryKey: QUERY_KEYS.allRewardCoupons(),
    queryFn: () => firestore.getAllRewardCoupons(),
  });
}

// ============================================================================
// SITE SETTINGS HOOKS
// ============================================================================

export function useSiteSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.siteSettings(),
    queryFn: () => firestore.getSiteSettings(),
  });
}

export function useUpdateSiteSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertSiteSettings) => firestore.updateSiteSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.siteSettings() });
    },
  });
}

// ============================================================================
// SYSTEM SETTINGS HOOKS
// ============================================================================

export function useSystemSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.systemSettings(),
    queryFn: () => firestore.getSystemSettings(),
  });
}

export function useInitializeSystemSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (adminId: string) => firestore.initializeSystemSettings(adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.systemSettings() });
    },
  });
}

export function useUpdateSystemSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data, adminId }: { data: Partial<InsertSystemSettings>; adminId: string }) => 
      firestore.updateSystemSettings(data, adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.systemSettings() });
    },
  });
}

// ============================================================================
// NOTIFICATION TEMPLATE HOOKS
// ============================================================================

export function useNotificationTemplates() {
  return useQuery({
    queryKey: QUERY_KEYS.notificationTemplates(),
    queryFn: () => firestore.getNotificationTemplates(),
  });
}

export function useCreateNotificationTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertNotificationTemplate) => firestore.createNotificationTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationTemplates() });
    },
  });
}

export function useUpdateNotificationTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data, adminId }: { id: string; data: Partial<InsertNotificationTemplate>; adminId?: string }) => 
      firestore.updateNotificationTemplate(id, data, adminId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationTemplates() });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.templateVersions(variables.id) });
      }
    },
  });
}

export function useTemplateVersionHistory(templateId: string | undefined) {
  return useQuery({
    queryKey: templateId ? QUERY_KEYS.templateVersions(templateId) : ["templateVersions", "null"],
    queryFn: () => (templateId ? firestore.getTemplateVersionHistory(templateId) : []),
    enabled: !!templateId,
  });
}

export function useDeleteNotificationTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => firestore.deleteNotificationTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationTemplates() });
    },
  });
}

// ============================================================================
// NOTIFICATION SOUNDS HOOKS
// ============================================================================

export function useNotificationSounds() {
  return useQuery({
    queryKey: QUERY_KEYS.notificationSounds(),
    queryFn: () => firestore.getNotificationSounds(),
  });
}

export function useActiveNotificationSounds() {
  return useQuery({
    queryKey: QUERY_KEYS.activeNotificationSounds(),
    queryFn: () => firestore.getActiveNotificationSounds(),
  });
}

export function useCreateNotificationSound() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertNotificationSound) => firestore.createNotificationSound(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationSounds() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeNotificationSounds() });
    },
  });
}

export function useUpdateNotificationSound() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertNotificationSound> }) => 
      firestore.updateNotificationSound(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationSounds() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeNotificationSounds() });
    },
  });
}

export function useDeleteNotificationSound() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => firestore.deleteNotificationSound(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationSounds() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeNotificationSounds() });
    },
  });
}

// ============================================================================
// GIFT HOOKS
// ============================================================================

export function useGifts() {
  return useQuery({
    queryKey: QUERY_KEYS.gifts(),
    queryFn: () => firestore.getGifts(),
  });
}

export function useActiveGifts() {
  return useQuery({
    queryKey: QUERY_KEYS.activeGifts(),
    queryFn: () => firestore.getActiveGifts(),
  });
}

export function useCreateGift() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertGift) => firestore.createGift(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gifts() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeGifts() });
    },
  });
}

export function useUpdateGift() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InsertGift> }) => 
      firestore.updateGift(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gifts() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeGifts() });
    },
  });
}

export function useDeleteGift() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => firestore.deleteGift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gifts() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeGifts() });
    },
  });
}

// ============================================================================
// FEEDBACK HOOKS
// ============================================================================

export function useAllFeedback() {
  return useQuery({
    queryKey: QUERY_KEYS.feedback(),
    queryFn: () => firestore.getAllFeedback(),
  });
}

export function useCreateFeedback() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertFeedback) => firestore.createFeedback(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.feedback() });
    },
  });
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Feedback> }) => 
      firestore.updateFeedback(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.feedback() });
    },
  });
}

export function useDeleteFeedback() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => firestore.deleteFeedback(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.feedback() });
    },
  });
}

// ============================================================================
// SMS HOOKS
// ============================================================================

export function useSmsTemplates() {
  return useQuery({
    queryKey: QUERY_KEYS.smsTemplates(),
    queryFn: () => firestore.getSmsTemplates(),
  });
}

export function useSeedDefaultSmsTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => firestore.seedDefaultSmsTemplates(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsTemplates() });
    },
  });
}

export function useSmsLogs() {
  return useQuery({
    queryKey: QUERY_KEYS.smsLogs(),
    queryFn: () => firestore.getSmsLogs(100),
  });
}

export function useSmsSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.smsSettings(),
    queryFn: () => firestore.getSmsSettings(),
  });
}

export function useUpdateSmsSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => firestore.updateSmsSettings(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsSettings() });
    },
  });
}

export function useCreateSmsSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => firestore.createSmsSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsSettings() });
    },
  });
}

export function useCreateSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => firestore.createSmsTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsTemplates() });
    },
  });
}

export function useUpdateSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => firestore.updateSmsTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsTemplates() });
    },
  });
}

export function useSendCampaignSms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: any) => {
      const { sendCampaignSms } = await import("@/lib/cloudFunctions");
      return sendCampaignSms(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsLogs() });
    },
  });
}

export function useSendTestSms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { phoneNumber: string; message: string }) => {
      try {
        console.log("📞 Calling sendTestSms Cloud Function...", params);
        const { sendTestSms } = await import("@/lib/cloudFunctions");
        const result = await sendTestSms(params);
        console.log("✅ sendTestSms result:", result);
        return result;
      } catch (error: any) {
        console.error("❌ sendTestSms error:", error);
        console.error("Error details:", {
          message: error.message,
          code: error.code,
          details: error.details,
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.smsLogs() });
    },
  });
}

export function useSmsBalance() {
  return useQuery({
    queryKey: QUERY_KEYS.smsBalance(),
    queryFn: async () => {
      try {
        console.log("📞 Calling getSmsBalance Cloud Function...");
        const { getSmsBalance } = await import("@/lib/cloudFunctions");
        const result = await getSmsBalance();
        console.log("✅ getSmsBalance result:", result);
        return result;
      } catch (error: any) {
        console.error("❌ getSmsBalance error:", error);
        console.error("Error details:", {
          message: error.message,
          code: error.code,
          details: error.details,
        });
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry on error
  });
}

// ============================================================================
// CAMPAIGN SMS TEMPLATE HOOKS
// ============================================================================

export function useCampaignSmsTemplates() {
  return useQuery({
    queryKey: QUERY_KEYS.campaignSmsTemplates(),
    queryFn: () => firestore.getCampaignSmsTemplates(),
  });
}

export function useCampaignSmsTemplate(id: string | undefined) {
  return useQuery({
    queryKey: id ? QUERY_KEYS.campaignSmsTemplate(id) : ["campaignSmsTemplate", "null"],
    queryFn: () => (id ? firestore.getCampaignSmsTemplate(id) : null),
    enabled: !!id,
  });
}

export function useCreateCampaignSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => firestore.createCampaignSmsTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaignSmsTemplates() });
    },
  });
}

export function useUpdateCampaignSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      firestore.updateCampaignSmsTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaignSmsTemplates() });
    },
  });
}

export function useDeleteCampaignSmsTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => firestore.deleteCampaignSmsTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaignSmsTemplates() });
    },
  });
}

export function useInitializeCampaignTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { initializeCampaignTemplates } = await import("@/lib/cloudFunctions");
      return initializeCampaignTemplates();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaignSmsTemplates() });
    },
  });
}

// ============================================================================
// INVENTORY MANAGEMENT HOOKS
// ============================================================================

export function useInventoryItems() {
  return useQuery({
    queryKey: QUERY_KEYS.inventory(),
    queryFn: () => firestore.getInventoryItems(),
  });
}

export function useInventoryItem(itemId: string | undefined) {
  return useQuery({
    queryKey: itemId ? QUERY_KEYS.inventoryItem(itemId) : ["inventory", "null"],
    queryFn: () => (itemId ? firestore.getInventoryItem(itemId) : null),
    enabled: !!itemId,
  });
}

export function useCriticalStockItems() {
  return useQuery({
    queryKey: QUERY_KEYS.criticalStock(),
    queryFn: () => firestore.getCriticalStockItems(),
  });
}

export function useCreateInventoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InsertInventoryItem) => firestore.createInventoryItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.criticalStock() });
    },
  });
}

export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Partial<InsertInventoryItem> }) =>
      firestore.updateInventoryItem(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.criticalStock() });
    },
  });
}

export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => firestore.deleteInventoryItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.criticalStock() });
    },
  });
}

export function useStockMovements(itemId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.stockMovements(itemId),
    queryFn: () => firestore.getStockMovements(itemId),
  });
}

export function useUpdateInventoryStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      itemId: string;
      quantity: number;
      type: "in" | "out" | "adjustment";
      reason: string;
      performedBy: string;
      relatedEntityId?: string;
      notes?: string;
    }) => firestore.updateInventoryStock(
      params.itemId,
      params.quantity,
      params.type,
      params.reason,
      params.performedBy,
      params.relatedEntityId,
      params.notes
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.inventory() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.criticalStock() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stockMovements() });
    },
  });
}

// ============================================================================
// WEB BUILDER HOOKS
// ============================================================================

export function useBrandSettings() {
  const { user } = useAuth();
  
  return useQuery<any | null>({
    queryKey: ["/api/brand-settings"],
    queryFn: async () => {
      const settings = await firestore.getBrandSettings();
      // Initialize default settings if none exist (admin only)
      if (!settings && user?.role === "admin") {
        return await firestore.initializeBrandSettings(user.id);
      }
      return settings;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useUpdateBrandSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update brand settings");
      }
      await firestore.updateBrandSettings(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brand-settings"] });
    },
  });
}

export function useFAQs(includeInactive = false) {
  return useQuery<any[]>({
    queryKey: ["/api/faqs", includeInactive],
    queryFn: async () => {
      return includeInactive ? await firestore.getAllFaqs() : await firestore.getFaqs();
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateFaq() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create FAQs");
      }
      return await firestore.createFaq({ ...data, updatedBy: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/faqs";
        }
      });
    },
  });
}

export function useUpdateFaq() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ faqId, data }: { faqId: string; data: any }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update FAQs");
      }
      await firestore.updateFaq(faqId, { ...data, updatedBy: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/faqs";
        }
      });
    },
  });
}

export function useDeleteFaq() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (faqId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete FAQs");
      }
      await firestore.deleteFaq(faqId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/faqs";
        }
      });
    },
  });
}

export function useFeatures(includeInactive = false) {
  return useQuery<any[]>({
    queryKey: ["/api/features", includeInactive],
    queryFn: async () => {
      return includeInactive ? await firestore.getAllFeatures() : await firestore.getFeatures();
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateFeature() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create features");
      }
      return await firestore.createFeature({ ...data, updatedBy: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/features";
        }
      });
    },
  });
}

export function useUpdateFeature() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ featureId, data }: { featureId: string; data: any }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update features");
      }
      await firestore.updateFeature(featureId, { ...data, updatedBy: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/features";
        }
      });
    },
  });
}

export function useDeleteFeature() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (featureId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete features");
      }
      await firestore.deleteFeature(featureId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/features";
        }
      });
    },
  });
}

export function useCleanupDuplicateFaqs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can cleanup duplicates");
      }
      return await firestore.cleanupDuplicateFaqs();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/faqs";
        }
      });
    },
  });
}

export function useCleanupDuplicateFeatures() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can cleanup duplicates");
      }
      return await firestore.cleanupDuplicateFeatures();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "/api/features";
        }
      });
    },
  });
}

export function useHomePageSections() {
  const { user } = useAuth();
  
  return useQuery<any[]>({
    queryKey: ["/api/homepage-sections"],
    queryFn: async () => {
      const sections = await firestore.getHomePageSections();
      // Initialize default sections if none exist (admin only)
      if (sections.length === 0 && user?.role === "admin") {
        await firestore.initializeHomePageSections(user.id);
        return await firestore.getHomePageSections();
      }
      // Ensure any missing sections are added (for existing installations)
      if (user?.role === "admin" && sections.length > 0) {
        const result = await firestore.ensureMissingHomePageSections(user.id);
        if (result.added.length > 0) {
          console.log("Added missing sections:", result.added);
          return await firestore.getHomePageSections();
        }
      }
      return sections;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateHomePageSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sectionId, data }: { sectionId: string; data: any }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update page sections");
      }
      await firestore.updateHomePageSection(sectionId, data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/homepage-sections"] });
    },
  });
}

// ============================================================================
// TESTIMONIALS
// ============================================================================

export function useTestimonials() {
  return useQuery<Testimonial[]>({
    queryKey: ["/api/testimonials"],
    queryFn: () => firestore.getTestimonials(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useAllTestimonials() {
  return useQuery<Testimonial[]>({
    queryKey: ["/api/testimonials", "all"],
    queryFn: () => firestore.getAllTestimonials(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useFeaturedTestimonials() {
  return useQuery<Testimonial[]>({
    queryKey: ["/api/testimonials", "featured"],
    queryFn: () => firestore.getFeaturedTestimonials(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateTestimonial() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InsertTestimonial) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create testimonials");
      }
      return await firestore.createTestimonial(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testimonials"] });
    },
  });
}

export function useUpdateTestimonial() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ testimonialId, data }: { testimonialId: string; data: Partial<InsertTestimonial> }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update testimonials");
      }
      await firestore.updateTestimonial(testimonialId, data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testimonials"] });
    },
  });
}

export function useDeleteTestimonial() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (testimonialId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete testimonials");
      }
      await firestore.deleteTestimonial(testimonialId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testimonials"] });
    },
  });
}

// ============================================================================
// CONTACT SETTINGS
// ============================================================================

export function useContactSettings() {
  return useQuery<ContactSettings | null>({
    queryKey: ["/api/contact-settings"],
    queryFn: () => firestore.getContactSettings(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateContactSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<InsertContactSettings>) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update contact settings");
      }
      await firestore.updateContactSettings(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-settings"] });
    },
  });
}

// ============================================================================
// GALLERY
// ============================================================================

export function useGalleryItems() {
  return useQuery<GalleryItem[]>({
    queryKey: ["/api/gallery"],
    queryFn: () => firestore.getGalleryItems(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useAllGalleryItems() {
  const { user } = useAuth();
  
  return useQuery<GalleryItem[]>({
    queryKey: ["/api/gallery", "all"],
    queryFn: () => firestore.getAllGalleryItems(),
    enabled: user?.role === "admin",
    staleTime: 1000 * 60 * 5,
  });
}

export function useFeaturedGalleryItems() {
  return useQuery<GalleryItem[]>({
    queryKey: ["/api/gallery", "featured"],
    queryFn: () => firestore.getFeaturedGalleryItems(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useGalleryItemsByCategory(category: string) {
  return useQuery<GalleryItem[]>({
    queryKey: ["/api/gallery", "category", category],
    queryFn: () => firestore.getGalleryItemsByCategory(category),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateGalleryItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InsertGalleryItem) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create gallery items");
      }
      return await firestore.createGalleryItem(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery"] });
    },
  });
}

export function useUpdateGalleryItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ galleryId, data }: { galleryId: string; data: Partial<InsertGalleryItem> }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update gallery items");
      }
      await firestore.updateGalleryItem(galleryId, data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery"] });
    },
  });
}

export function useDeleteGalleryItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (galleryId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete gallery items");
      }
      await firestore.deleteGalleryItem(galleryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery"] });
    },
  });
}

export function useGalleryPageSettings() {
  return useQuery<GalleryPageSettings | null>({
    queryKey: ["/api/gallery", "pageSettings"],
    queryFn: () => firestore.getGalleryPageSettings(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateGalleryPageSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Partial<InsertGalleryPageSettings>) => 
      firestore.updateGalleryPageSettings(data, user?.id || "unknown"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery", "pageSettings"] });
    },
  });
}

// ============================================================================
// GALLERY CATEGORIES (Dynamic Categories)
// ============================================================================

export function useGalleryCategories() {
  const { user } = useAuth();
  
  return useQuery<GalleryCategory_Type[]>({
    queryKey: ["/api/gallery", "categories"],
    queryFn: () => firestore.getGalleryCategories(),
    enabled: user?.role === "admin",
    staleTime: 1000 * 60 * 5,
  });
}

export function useActiveGalleryCategories() {
  return useQuery<GalleryCategory_Type[]>({
    queryKey: ["/api/gallery", "categories", "active"],
    queryFn: () => firestore.getActiveGalleryCategories(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateGalleryCategory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InsertGalleryCategory) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create gallery categories");
      }
      return await firestore.createGalleryCategory(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery", "categories"] });
    },
  });
}

export function useUpdateGalleryCategory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertGalleryCategory> }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update gallery categories");
      }
      await firestore.updateGalleryCategory(id, data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery", "categories"] });
    },
  });
}

export function useDeleteGalleryCategory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (categoryId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete gallery categories");
      }
      await firestore.deleteGalleryCategory(categoryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gallery", "categories"] });
    },
  });
}

// ============================================================================
// DEPLOYMENT SETTINGS
// ============================================================================

export function useDeploymentSettings() {
  const { user } = useAuth();
  
  return useQuery<DeploymentSettings | null>({
    queryKey: QUERY_KEYS.deploymentSettings(),
    queryFn: () => firestore.getDeploymentSettings(),
    enabled: user?.role === "admin",
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateDeploymentSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<InsertDeploymentSettings>) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update deployment settings");
      }
      await firestore.updateDeploymentSettings(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.deploymentSettings() });
    },
  });
}

// ============================================================================
// BLOG POSTS
// ============================================================================

export function useBlogPosts() {
  return useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
    queryFn: () => firestore.getPublishedBlogPosts(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useAllBlogPosts() {
  const { user } = useAuth();
  
  return useQuery<BlogPost[]>({
    queryKey: ["/api/blog", "all"],
    queryFn: () => firestore.getAllBlogPosts(),
    enabled: user?.role === "admin",
    staleTime: 1000 * 60 * 5,
  });
}

export function useBlogPostBySlug(slug: string) {
  return useQuery<BlogPost | null>({
    queryKey: ["/api/blog", slug],
    queryFn: () => firestore.getBlogPostBySlug(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
}

export function useBlogPostsByCategory(category: string) {
  return useQuery<BlogPost[]>({
    queryKey: ["/api/blog", "category", category],
    queryFn: () => firestore.getBlogPostsByCategory(category),
    enabled: !!category,
    staleTime: 1000 * 60 * 5,
  });
}

export function useBlogPostsByTag(tag: string) {
  return useQuery<BlogPost[]>({
    queryKey: ["/api/blog", "tag", tag],
    queryFn: () => firestore.getBlogPostsByTag(tag),
    enabled: !!tag,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateBlogPost() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InsertBlogPost) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create blog posts");
      }
      return await firestore.createBlogPost(data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/blog"] });
    },
  });
}

export function useUpdateBlogPost() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ blogId, data }: { blogId: string; data: Partial<InsertBlogPost> }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update blog posts");
      }
      await firestore.updateBlogPost(blogId, data, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/blog"] });
    },
  });
}

export function usePublishBlogPost() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (blogId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can publish blog posts");
      }
      await firestore.publishBlogPost(blogId, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/blog"] });
    },
  });
}

export function useDeleteBlogPost() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (blogId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete blog posts");
      }
      await firestore.deleteBlogPost(blogId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/blog"] });
    },
  });
}

// ============================================================================
// POPUP HOOKS
// ============================================================================

export function usePopups() {
  return useQuery<Popup[]>({
    queryKey: QUERY_KEYS.popups(),
    queryFn: () => firestore.getAllPopups(),
    staleTime: 1000 * 60 * 5,
  });
}

export function usePopupsByPage(pageId: string) {
  return useQuery<Popup[]>({
    queryKey: QUERY_KEYS.popupsByPage(pageId),
    queryFn: () => firestore.getPopupsByPage(pageId),
    enabled: !!pageId,
    staleTime: 1000 * 60 * 5,
  });
}

export function usePopup(popupId: string) {
  return useQuery<Popup | null>({
    queryKey: QUERY_KEYS.popup(popupId),
    queryFn: () => firestore.getPopup(popupId),
    enabled: !!popupId,
  });
}

export function useCreatePopup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: InsertPopup) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can create popups");
      }
      return await firestore.createPopup(data);
    },
    onSuccess: () => {
      // Invalidate all popup-related queries (popups list and page-specific queries)
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      queryClient.invalidateQueries({ queryKey: ["popup"] });
    },
  });
}

export function useUpdatePopup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ popupId, data }: { popupId: string; data: Partial<InsertPopup> }) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can update popups");
      }
      await firestore.updatePopup(popupId, data);
    },
    onSuccess: () => {
      // Invalidate all popup-related queries
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      queryClient.invalidateQueries({ queryKey: ["popup"] });
    },
  });
}

export function useDeletePopup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (popupId: string) => {
      if (!user || user.role !== "admin") {
        throw new Error("Only admins can delete popups");
      }
      await firestore.deletePopup(popupId);
    },
    onSuccess: () => {
      // Invalidate all popup-related queries
      queryClient.invalidateQueries({ queryKey: ["popups"] });
      queryClient.invalidateQueries({ queryKey: ["popup"] });
    },
  });
}

// ============================================================================
// EMAIL TEMPLATE HOOKS
// ============================================================================

export function useEmailTemplates() {
  return useQuery({
    queryKey: QUERY_KEYS.emailTemplates(),
    queryFn: () => firestore.getEmailTemplates(),
  });
}

export function useEmailLogs() {
  return useQuery({
    queryKey: QUERY_KEYS.emailLogs(),
    queryFn: () => firestore.getEmailLogs(100),
  });
}

export function useEmailSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.emailSettings(),
    queryFn: () => firestore.getEmailSettings(),
  });
}

export function useUpdateEmailSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => firestore.updateEmailSettings(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.emailSettings() });
    },
  });
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => firestore.createEmailTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.emailTemplates() });
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => firestore.updateEmailTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.emailTemplates() });
    },
  });
}

// ============================================================================
// CORPORATE PRICING HOOKS
// ============================================================================

export function useCorporatePricing(companyId: string) {
  return useQuery<CorporatePricing[]>({
    queryKey: QUERY_KEYS.corporatePricing(companyId),
    queryFn: () => firestore.getCorporatePricing(companyId),
    enabled: !!companyId,
  });
}

export function useAllCorporatePricing() {
  return useQuery<CorporatePricing[]>({
    queryKey: QUERY_KEYS.allCorporatePricing(),
    queryFn: () => firestore.getAllCorporatePricing(),
  });
}

export function useCreateCorporatePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InsertCorporatePricing) => firestore.createCorporatePricing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporatePricing() });
    },
  });
}

export function useUpdateCorporatePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pricingId, data }: { pricingId: string; data: Partial<InsertCorporatePricing> }) =>
      firestore.updateCorporatePricing(pricingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporatePricing() });
    },
  });
}

export function useDeleteCorporatePricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pricingId: string) => firestore.deleteCorporatePricing(pricingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporatePricing() });
    },
  });
}

// ============================================================================
// CORPORATE INVOICE HOOKS
// ============================================================================

export function useCorporateInvoices(companyId: string) {
  return useQuery<CorporateInvoice[]>({
    queryKey: QUERY_KEYS.corporateInvoices(companyId),
    queryFn: () => firestore.getCorporateInvoices(companyId),
    enabled: !!companyId,
  });
}

export function useAllCorporateInvoices() {
  return useQuery<CorporateInvoice[]>({
    queryKey: QUERY_KEYS.allCorporateInvoices(),
    queryFn: () => firestore.getAllCorporateInvoices(),
  });
}

export function useCreateCorporateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InsertCorporateInvoice) => firestore.createCorporateInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporateInvoices() });
    },
  });
}

export function useUpdateCorporateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, data }: { invoiceId: string; data: Partial<InsertCorporateInvoice> }) =>
      firestore.updateCorporateInvoice(invoiceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporateInvoices() });
    },
  });
}

export function useDeleteCorporateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => firestore.deleteCorporateInvoice(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCorporateInvoices() });
    },
  });
}

// ============================================================================
// COMPANY-SPECIFIC DATA HOOKS
// ============================================================================

export function useCompanyWashes(companyId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.companyWashes(companyId),
    queryFn: () => firestore.getCompanyWashes(companyId),
    enabled: !!companyId,
  });
}

export function useCompanyPayments(companyId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.companyPayments(companyId),
    queryFn: () => firestore.getCompanyPayments(companyId),
    enabled: !!companyId,
  });
}

// ============================================================================
// USER NOTIFICATION HOOKS
// ============================================================================

export function useUserNotifications(userId: string) {
  return useQuery<UserNotification[]>({
    queryKey: QUERY_KEYS.userNotifications(userId),
    queryFn: () => firestore.getUserNotifications(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes - FCM push handles real-time
    gcTime: 10 * 60 * 1000,
    // Removed 30s polling - FCM push notifications handle real-time updates
  });
}

export function useUserNotification(userId: string, notificationId: string) {
  return useQuery<UserNotification | null>({
    queryKey: QUERY_KEYS.userNotification(userId, notificationId),
    queryFn: () => firestore.getUserNotification(userId, notificationId),
    enabled: !!userId && !!notificationId,
  });
}

export function useCreateUserNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: InsertUserNotification }) =>
      firestore.createUserNotification(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userNotifications(userId) });
    },
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, notificationId }: { userId: string; notificationId: string }) =>
      firestore.markNotificationAsRead(userId, notificationId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userNotifications(userId) });
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => firestore.markAllNotificationsAsRead(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userNotifications(userId) });
    },
  });
}

export function useDeleteUserNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, notificationId }: { userId: string; notificationId: string }) =>
      firestore.deleteUserNotification(userId, notificationId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userNotifications(userId) });
    },
  });
}

// ============================================================================
// CUSTOMER ANALYTICS HOOKS
// ============================================================================

export function useCustomerAnalytics(customerId: string) {
  return useQuery<CustomerAnalytics | null>({
    queryKey: QUERY_KEYS.customerAnalytics(customerId),
    queryFn: () => firestore.getCustomerAnalytics(customerId),
    enabled: !!customerId,
  });
}

export function useAllCustomerAnalytics() {
  return useQuery<CustomerAnalytics[]>({
    queryKey: QUERY_KEYS.allCustomerAnalytics(),
    queryFn: () => firestore.getAllCustomerAnalytics(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useTopCustomersBySpending(limit: number = 10) {
  return useQuery<CustomerAnalytics[]>({
    queryKey: QUERY_KEYS.topCustomersBySpending(limit),
    queryFn: () => firestore.getTopCustomersBySpending(limit),
  });
}

export function useTopCustomersByVisits(limit: number = 10) {
  return useQuery<CustomerAnalytics[]>({
    queryKey: QUERY_KEYS.topCustomersByVisits(limit),
    queryFn: () => firestore.getTopCustomersByVisits(limit),
  });
}

export function useVIPCustomers() {
  return useQuery<CustomerAnalytics[]>({
    queryKey: QUERY_KEYS.vipCustomers(),
    queryFn: () => firestore.getVIPCustomers(),
  });
}

export function useCreateOrUpdateCustomerAnalytics() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: Partial<InsertCustomerAnalytics> }) =>
      firestore.createOrUpdateCustomerAnalytics(customerId, data),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerAnalytics(customerId) });
      queryClient.invalidateQueries({ queryKey: ["customerAnalytics"] });
    },
  });
}

// ============================================================================
// CORPORATE AGREEMENT HOOKS
// ============================================================================

export function useCorporateAgreement(agreementId: string) {
  return useQuery<CorporateAgreement | null>({
    queryKey: QUERY_KEYS.corporateAgreement(agreementId),
    queryFn: () => firestore.getCorporateAgreement(agreementId),
    enabled: !!agreementId,
  });
}

export function useCorporateAgreementsByCompany(companyId: string) {
  return useQuery<CorporateAgreement[]>({
    queryKey: QUERY_KEYS.corporateAgreementsByCompany(companyId),
    queryFn: () => firestore.getCorporateAgreementsByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useActiveAgreementsByCompany(companyId: string) {
  return useQuery<CorporateAgreement[]>({
    queryKey: QUERY_KEYS.activeAgreementsByCompany(companyId),
    queryFn: () => firestore.getActiveAgreementsByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useAllCorporateAgreements() {
  return useQuery<CorporateAgreement[]>({
    queryKey: QUERY_KEYS.allCorporateAgreements(),
    queryFn: () => firestore.getAllCorporateAgreements(),
  });
}

export function useExpiringAgreements(daysUntilExpiry: number = 30) {
  return useQuery<CorporateAgreement[]>({
    queryKey: QUERY_KEYS.expiringAgreements(daysUntilExpiry),
    queryFn: () => firestore.getExpiringAgreements(daysUntilExpiry),
  });
}

export function useCreateCorporateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InsertCorporateAgreement) => firestore.createCorporateAgreement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corporateAgreements"] });
    },
  });
}

export function useUpdateCorporateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agreementId, data }: { agreementId: string; data: Partial<InsertCorporateAgreement> }) =>
      firestore.updateCorporateAgreement(agreementId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corporateAgreements"] });
    },
  });
}

export function useDeleteCorporateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agreementId: string) => firestore.deleteCorporateAgreement(agreementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["corporateAgreements"] });
    },
  });
}

// ============================================================================
// VEHICLE ANALYTICS HOOKS
// ============================================================================

export function useVehicleAnalytics(vehicleId: string) {
  return useQuery<VehicleAnalytics | null>({
    queryKey: QUERY_KEYS.vehicleAnalytics(vehicleId),
    queryFn: () => firestore.getVehicleAnalytics(vehicleId),
    enabled: !!vehicleId,
  });
}

export function useAllVehicleAnalytics() {
  return useQuery<VehicleAnalytics[]>({
    queryKey: QUERY_KEYS.allVehicleAnalytics(),
    queryFn: () => firestore.getAllVehicleAnalytics(),
  });
}

export function useTopVehiclesByWashCount(limit: number = 10) {
  return useQuery<VehicleAnalytics[]>({
    queryKey: QUERY_KEYS.topVehiclesByWashCount(limit),
    queryFn: () => firestore.getTopVehiclesByWashCount(limit),
  });
}

export function useTopVehiclesByRevenue(limit: number = 10) {
  return useQuery<VehicleAnalytics[]>({
    queryKey: QUERY_KEYS.topVehiclesByRevenue(limit),
    queryFn: () => firestore.getTopVehiclesByRevenue(limit),
  });
}

export function useCreateOrUpdateVehicleAnalytics() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, data }: { vehicleId: string; data: Partial<InsertVehicleAnalytics> }) =>
      firestore.createOrUpdateVehicleAnalytics(vehicleId, data),
    onSuccess: (_, { vehicleId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.vehicleAnalytics(vehicleId) });
      queryClient.invalidateQueries({ queryKey: ["vehicleAnalytics"] });
    },
  });
}

// ============================================================================
// ADVANCED ANALYTICS HOOKS
// ============================================================================

export function useCustomerWashHistory(customerId: string) {
  return useQuery<Wash[]>({
    queryKey: QUERY_KEYS.customerWashHistory(customerId),
    queryFn: () => firestore.getCustomerWashHistory(customerId),
    enabled: !!customerId,
  });
}

export function useVehicleWashHistory(vehicleId: string) {
  return useQuery<Wash[]>({
    queryKey: QUERY_KEYS.vehicleWashHistory(vehicleId),
    queryFn: () => firestore.getVehicleWashHistory(vehicleId),
    enabled: !!vehicleId,
  });
}

export function useVehicleAppointments(vehicleId: string | undefined) {
  return useQuery({
    queryKey: vehicleId ? QUERY_KEYS.vehicleAppointments(vehicleId) : ["vehicleAppointments", "null"],
    queryFn: () => (vehicleId ? firestore.getVehicleAppointments(vehicleId) : []),
    enabled: !!vehicleId,
  });
}

export function useSearchVehicleByPlate(plateNumber: string) {
  return useQuery({
    queryKey: QUERY_KEYS.vehicleByPlate(plateNumber),
    queryFn: () => firestore.searchVehicleByPlate(plateNumber),
    enabled: plateNumber.length >= 3,
  });
}

// ============================================================================
// SERVER-SIDE AGGREGATED ANALYTICS HOOKS
// ============================================================================

/**
 * Hook for fetching daily analytics (pre-aggregated by Cloud Functions)
 * @param dateStr Date in YYYY-MM-DD format
 * @param enabled Optional - whether to enable the query (default: true)
 * @returns useQuery hook with DailyAnalytics data
 */
export function useDailyAnalytics(dateStr: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["analytics", "daily", dateStr],
    queryFn: () => firestore.getDailyAnalytics(dateStr),
    enabled: enabled && !!dateStr,
    staleTime: 60 * 60 * 1000, // 1 hour (daily data doesn't change frequently)
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

/**
 * Hook for fetching monthly analytics (pre-aggregated by Cloud Functions)
 * @param monthStr Month in YYYY-MM format (e.g., "2025-11")
 * @param enabled Optional - whether to enable the query (default: true)
 * @returns useQuery hook with MonthlyAnalytics data
 */
export function useMonthlyAnalytics(monthStr: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["analytics", "monthly", monthStr],
    queryFn: () => firestore.getMonthlyAnalytics(monthStr),
    enabled: enabled && !!monthStr,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (monthly data doesn't change)
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  });
}

/**
 * Hook for fetching analytics for a date range
 * @param startDate Start date in YYYY-MM-DD format
 * @param endDate End date in YYYY-MM-DD format
 * @param enabled Optional - whether to enable the query (default: true)
 * @returns useQuery hook with array of DailyAnalytics
 */
export function useAnalyticsByDateRange(
  startDate: string,
  endDate: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ["analytics", "dateRange", startDate, endDate],
    queryFn: () => firestore.getAnalyticsByDateRange(startDate, endDate),
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });
}

// ============================================================================
// WEB BUILDER - NAVIGATION HOOKS
// ============================================================================

export function useNavigationItems() {
  return useQuery({
    queryKey: ["navigation"],
    queryFn: async () => {
      const snapshot = await getDocs(
        query(collection(db, "navigation"), orderBy("order", "asc"))
      );
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },
  });
}

export function useCreateNavigationItem() {
  return useMutation({
    mutationFn: async (data: any) => {
      const timestamp = Date.now();
      const docRef = await addDoc(collection(db, "navigation"), {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return { id: docRef.id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    },
  });
}

export function useUpdateNavigationItem() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await updateDoc(doc(db, "navigation", id), {
        ...data,
        updatedAt: Date.now(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    },
  });
}

export function useDeleteNavigationItem() {
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, "navigation", id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    },
  });
}

export function useReorderNavigationItems() {
  return useMutation({
    mutationFn: async (items: Array<{ id: string; order: number }>) => {
      const batch = writeBatch(db);
      items.forEach((item) => {
        batch.update(doc(db, "navigation", item.id), { 
          order: item.order,
          updatedAt: Date.now(),
        });
      });
      await batch.commit();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    },
  });
}

// ============================================================================
// WEB BUILDER - CUSTOM PAGES HOOKS
// ============================================================================

export function useCustomPages(status?: string) {
  return useQuery({
    queryKey: ["customPages", status],
    queryFn: async () => {
      let q = collection(db, "custom_pages");
      if (status) {
        q = query(collection(db, "custom_pages"), where("status", "==", status)) as any;
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },
  });
}

export function useCustomPage(pageId: string) {
  return useQuery({
    queryKey: ["customPages", pageId],
    queryFn: async () => {
      const docSnap = await getDoc(doc(db, "custom_pages", pageId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    },
    enabled: !!pageId,
  });
}

export function useCustomPageBySlug(slug: string) {
  return useQuery({
    queryKey: ["customPages", "slug", slug],
    queryFn: async () => {
      const q = query(
        collection(db, "custom_pages"),
        where("slug", "==", slug),
        where("status", "==", "published")
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }
      return null;
    },
    enabled: !!slug,
  });
}

export function useCreateCustomPage() {
  return useMutation({
    mutationFn: async (data: any) => {
      const timestamp = Date.now();
      const docRef = await addDoc(collection(db, "custom_pages"), {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return { id: docRef.id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customPages"] });
    },
  });
}

export function useUpdateCustomPage() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await updateDoc(doc(db, "custom_pages", id), {
        ...data,
        updatedAt: Date.now(),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["customPages"] });
      queryClient.invalidateQueries({ queryKey: ["customPages", variables.id] });
    },
  });
}

export function useDeleteCustomPage() {
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, "custom_pages", id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customPages"] });
    },
  });
}

export function usePublishPage() {
  return useMutation({
    mutationFn: async (pageId: string) => {
      await updateDoc(doc(db, "custom_pages", pageId), {
        status: "published",
        publishedAt: Date.now(),
        updatedAt: Date.now(),
      });
    },
    onSuccess: (_, pageId) => {
      queryClient.invalidateQueries({ queryKey: ["customPages"] });
      queryClient.invalidateQueries({ queryKey: ["customPages", pageId] });
    },
  });
}

// ============================================================================
// WEB BUILDER - PAGE SECTIONS HOOKS
// ============================================================================

export function usePageSections(pageId: string) {
  return useQuery({
    queryKey: ["pageSections", pageId],
    queryFn: async () => {
      const q = query(
        collection(db, "page_sections"),
        where("pageId", "==", pageId),
        orderBy("order", "asc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },
    enabled: !!pageId,
  });
}

export function useCreatePageSection() {
  return useMutation({
    mutationFn: async (data: any) => {
      const timestamp = Date.now();
      const docRef = await addDoc(collection(db, "page_sections"), {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return { id: docRef.id, ...data };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pageSections", data.pageId] });
    },
  });
}

export function useUpdatePageSection() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await updateDoc(doc(db, "page_sections", id), {
        ...data,
        updatedAt: Date.now(),
      });
    },
    onSuccess: (_, variables) => {
      if (variables.data.pageId) {
        queryClient.invalidateQueries({ queryKey: ["pageSections", variables.data.pageId] });
      }
    },
  });
}

export function useDeletePageSection() {
  return useMutation({
    mutationFn: async ({ id, pageId }: { id: string; pageId: string }) => {
      await deleteDoc(doc(db, "page_sections", id));
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pageSections", variables.pageId] });
    },
  });
}

export function useReorderPageSections() {
  return useMutation({
    mutationFn: async ({ pageId, sections }: { pageId: string; sections: Array<{ id: string; order: number }> }) => {
      const batch = writeBatch(db);
      sections.forEach((section) => {
        batch.update(doc(db, "page_sections", section.id), { 
          order: section.order,
          updatedAt: Date.now(),
        });
      });
      await batch.commit();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pageSections", variables.pageId] });
    },
  });
}

// ============================================================================
// WEB BUILDER - SITE TEMPLATES HOOKS
// ============================================================================

export function useSiteTemplates(category?: string) {
  return useQuery({
    queryKey: ["siteTemplates", category],
    queryFn: async () => {
      let q = query(collection(db, "site_templates"), where("isPublic", "==", true));
      if (category) {
        q = query(q, where("category", "==", category)) as any;
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },
  });
}

export function useSiteTemplate(templateId: string) {
  return useQuery({
    queryKey: ["siteTemplates", templateId],
    queryFn: async () => {
      const docSnap = await getDoc(doc(db, "site_templates", templateId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    },
    enabled: !!templateId,
  });
}

export function useCreateSiteTemplate() {
  return useMutation({
    mutationFn: async (data: any) => {
      const timestamp = Date.now();
      const docRef = await addDoc(collection(db, "site_templates"), {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return { id: docRef.id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["siteTemplates"] });
    },
  });
}

export function useApplySiteTemplate() {
  return useMutation({
    mutationFn: async (templateId: string) => {
      const templateSnap = await getDoc(doc(db, "site_templates", templateId));
      if (!templateSnap.exists()) throw new Error("Template not found");
      
      const template = templateSnap.data();
      const timestamp = Date.now();
      
      // Apply brand settings
      if (template.brandSettings) {
        const brandDoc = doc(db, "settings", "brand");
        await setDoc(brandDoc, {
          ...template.brandSettings,
          updatedAt: timestamp,
        }, { merge: true });
      }
      
      // Create pages
      if (template.pages && template.pages.length > 0) {
        const batch = writeBatch(db);
        template.pages.forEach((page: any) => {
          const pageRef = doc(collection(db, "custom_pages"));
          batch.set(pageRef, {
            ...page,
            createdAt: timestamp,
            updatedAt: timestamp,
          });
        });
        await batch.commit();
      }
      
      // Create navigation
      if (template.navigation && template.navigation.length > 0) {
        const batch = writeBatch(db);
        template.navigation.forEach((navItem: any) => {
          const navRef = doc(collection(db, "navigation"));
          batch.set(navRef, {
            ...navItem,
            createdAt: timestamp,
            updatedAt: timestamp,
          });
        });
        await batch.commit();
      }
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brandSettings"] });
      queryClient.invalidateQueries({ queryKey: ["customPages"] });
      queryClient.invalidateQueries({ queryKey: ["navigation"] });
    },
  });
}

// ============================================================================
// WEB BUILDER - ANALYTICS SETTINGS HOOKS
// ============================================================================

export function useAnalyticsSettings() {
  return useQuery({
    queryKey: ["analyticsSettings"],
    queryFn: async () => {
      const docSnap = await getDoc(doc(db, "settings", "analytics"));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    },
  });
}

export function useUpdateAnalyticsSettings() {
  return useMutation({
    mutationFn: async (data: any) => {
      await setDoc(
        doc(db, "settings", "analytics"),
        {
          ...data,
          updatedAt: Date.now(),
        },
        { merge: true }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyticsSettings"] });
    },
  });
}

// ============================================================================
// WEB BUILDER - CUSTOM FORMS HOOKS
// ============================================================================

export function useCustomForms() {
  return useQuery({
    queryKey: ["customForms"],
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, "custom_forms"));
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    },
  });
}

export function useCustomForm(formId: string) {
  return useQuery({
    queryKey: ["customForms", formId],
    queryFn: async () => {
      const docSnap = await getDoc(doc(db, "custom_forms", formId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    },
    enabled: !!formId,
  });
}

export function useCreateCustomForm() {
  return useMutation({
    mutationFn: async (data: any) => {
      const timestamp = Date.now();
      const docRef = await addDoc(collection(db, "custom_forms"), {
        ...data,
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      return { id: docRef.id, ...data };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customForms"] });
    },
  });
}

export function useUpdateCustomForm() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await updateDoc(doc(db, "custom_forms", id), {
        ...data,
        updatedAt: Date.now(),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["customForms"] });
      queryClient.invalidateQueries({ queryKey: ["customForms", variables.id] });
    },
  });
}

export function useDeleteCustomForm() {
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, "custom_forms", id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customForms"] });
    },
  });
}

// ============================================================================
// SUBSCRIPTION PACKAGE HOOKS
// ============================================================================

export function useSubscriptionPackages() {
  return useQuery({
    queryKey: QUERY_KEYS.subscriptionPackages(),
    queryFn: () => firestore.getAllSubscriptionPackages(),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useActiveSubscriptionPackages() {
  return useQuery({
    queryKey: QUERY_KEYS.activeSubscriptionPackages(),
    queryFn: () => firestore.getActiveSubscriptionPackages(),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useSubscriptionPackage(packageId: string | undefined) {
  return useQuery({
    queryKey: packageId ? QUERY_KEYS.subscriptionPackage(packageId) : ["subscriptionPackage", "null"],
    queryFn: () => (packageId ? firestore.getSubscriptionPackage(packageId) : null),
    enabled: !!packageId,
  });
}

export function useCreateSubscriptionPackage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertSubscriptionPackage) => firestore.createSubscriptionPackage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionPackages() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeSubscriptionPackages() });
    },
  });
}

export function useUpdateSubscriptionPackage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ packageId, data }: { packageId: string; data: Partial<SubscriptionPackage> }) =>
      firestore.updateSubscriptionPackage(packageId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionPackages() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeSubscriptionPackages() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionPackage(variables.packageId) });
    },
  });
}

export function useDeleteSubscriptionPackage() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (packageId: string) => firestore.deleteSubscriptionPackage(packageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionPackages() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeSubscriptionPackages() });
    },
  });
}

// ============================================================================
// CUSTOMER SUBSCRIPTION HOOKS
// ============================================================================

export function useCustomerSubscriptions(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.customerSubscriptions(userId) : ["customerSubscriptions", "null"],
    queryFn: () => (userId ? firestore.getCustomerSubscriptions(userId) : []),
    enabled: !!userId,
  });
}

export function useActiveCustomerSubscriptions(userId: string | undefined) {
  return useQuery({
    queryKey: userId ? QUERY_KEYS.activeCustomerSubscriptions(userId) : ["customerSubscriptions", "null", "active"],
    queryFn: () => (userId ? firestore.getActiveCustomerSubscriptions(userId) : []),
    enabled: !!userId,
  });
}

export function usePendingCustomerSubscriptions() {
  return useQuery({
    queryKey: ['pendingCustomerSubscriptions'],
    queryFn: () => firestore.getPendingCustomerSubscriptions(),
  });
}

export function useCustomerSubscription(subscriptionId: string | undefined) {
  return useQuery({
    queryKey: subscriptionId ? QUERY_KEYS.customerSubscription(subscriptionId) : ["customerSubscription", "null"],
    queryFn: () => (subscriptionId ? firestore.getCustomerSubscription(subscriptionId) : null),
    enabled: !!subscriptionId,
  });
}

export function useCompanySubscriptions(companyId: string | undefined) {
  return useQuery({
    queryKey: companyId ? QUERY_KEYS.companySubscriptions(companyId) : ["customerSubscriptions", "company", "null"],
    queryFn: () => (companyId ? firestore.getCompanySubscriptions(companyId) : []),
    enabled: !!companyId,
  });
}

export function useAllCustomerSubscriptions() {
  return useQuery({
    queryKey: QUERY_KEYS.allCustomerSubscriptions(),
    queryFn: () => firestore.getAllCustomerSubscriptions(),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useExpiringSubscriptions(days: number = 7) {
  return useQuery({
    queryKey: QUERY_KEYS.expiringSubscriptions(days),
    queryFn: () => firestore.getExpiringSubscriptions(days),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateCustomerSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: InsertCustomerSubscription) => firestore.createCustomerSubscription(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(variables.userId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
      if (variables.companyId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companySubscriptions(variables.companyId) });
      }
    },
  });
}

export function useUpdateCustomerSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, data }: { subscriptionId: string; data: Partial<CustomerSubscription> }) =>
      firestore.updateCustomerSubscription(subscriptionId, data),
    onSuccess: async (result, variables) => {
      const subscription = await firestore.getCustomerSubscription(variables.subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(variables.subscriptionId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
        if (subscription.companyId) {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companySubscriptions(subscription.companyId) });
        }
      }
    },
  });
}

export function useCancelCustomerSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId: string) => firestore.cancelCustomerSubscription(subscriptionId),
    onSuccess: async (result, subscriptionId) => {
      const subscription = await firestore.getCustomerSubscription(subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(subscriptionId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
      }
    },
  });
}

export function usePauseCustomerSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId: string) => firestore.pauseCustomerSubscription(subscriptionId),
    onSuccess: async (result, subscriptionId) => {
      const subscription = await firestore.getCustomerSubscription(subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(subscriptionId) });
      }
    },
  });
}

export function useResumeCustomerSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId: string) => firestore.resumeCustomerSubscription(subscriptionId),
    onSuccess: async (result, subscriptionId) => {
      const subscription = await firestore.getCustomerSubscription(subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(subscriptionId) });
      }
    },
  });
}

export function useRecordSubscriptionPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      subscriptionId,
      paymentMethod,
      cashierId,
    }: {
      subscriptionId: string;
      paymentMethod: string;
      cashierId: string;
    }) => {
      await firestore.recordSubscriptionPayment(subscriptionId, paymentMethod, cashierId);
    },
    onSuccess: async (result, { subscriptionId }) => {
      const subscription = await firestore.getCustomerSubscription(subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(subscriptionId) });
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionAnalytics() });
      queryClient.invalidateQueries({ queryKey: ['subscription_payment_records'] });
    },
  });
}

export function useSubscriptionPaymentRecords() {
  return useQuery<firestore.SubscriptionPaymentRecord[]>({
    queryKey: ['subscription_payment_records'],
    queryFn: firestore.getSubscriptionPaymentRecords,
  });
}

export function useApproveSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (subscriptionId: string) => firestore.approveCustomerSubscription(subscriptionId),
    onSuccess: (result, subscriptionId) => {
      // Immediately invalidate all subscription caches for instant UI update
      queryClient.invalidateQueries({ queryKey: ['pendingCustomerSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(subscriptionId) });
      
      // Fetch subscription data in background to invalidate user-specific caches
      firestore.getCustomerSubscription(subscriptionId).then(subscription => {
        if (subscription) {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        }
      });
    },
  });
}

/**
 * Create subscription purchase (Cashier flow)
 */
export function useCreateSubscriptionPurchase() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      customerId, 
      packageId, 
      paymentMethod 
    }: { 
      customerId: string; 
      packageId: string; 
      paymentMethod: string; 
    }) => {
      // Dynamic import to avoid tree-shaking issues in production
      const { httpsCallable: callableFunc } = await import("firebase/functions");
      const { functions: funcs } = await import("@/lib/firebase");
      
      const createPurchase = callableFunc(funcs, "createSubscriptionPurchase");
      const result = await createPurchase({ 
        customerId, 
        packageId, 
        paymentMethod,
        cashierUserId: user?.id 
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingCustomerSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: ["customer_subscriptions"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },
  });
}

export function useUseSubscriptionService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, serviceType, washId, vehicleId, serviceId, serviceName }: {
      subscriptionId: string;
      serviceType: "exterior" | "interior" | "detailing" | "extra";
      washId: string;
      vehicleId: string;
      serviceId: string;
      serviceName: string;
    }) => firestore.useSubscriptionService(subscriptionId, serviceType, washId, vehicleId, serviceId, serviceName),
    onSuccess: async (result, variables) => {
      const subscription = await firestore.getCustomerSubscription(variables.subscriptionId);
      if (subscription) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscription(variables.subscriptionId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.customerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activeCustomerSubscriptions(subscription.userId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionUsage(variables.subscriptionId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allSubscriptionUsage() });
      }
    },
  });
}

// ============================================================================
// SUBSCRIPTION USAGE HOOKS
// ============================================================================

export function useSubscriptionUsage(subscriptionId: string | undefined) {
  return useQuery({
    queryKey: subscriptionId ? QUERY_KEYS.subscriptionUsage(subscriptionId) : ["subscriptionUsage", "null"],
    queryFn: () => (subscriptionId ? firestore.getSubscriptionUsage(subscriptionId) : []),
    enabled: !!subscriptionId,
  });
}

export function useAllSubscriptionUsage() {
  return useQuery({
    queryKey: QUERY_KEYS.allSubscriptionUsage(),
    queryFn: () => firestore.getAllSubscriptionUsage(),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// ============================================================================
// CORPORATE BUDGET HOOKS
// ============================================================================

export function useCurrentBudget(corporateCustomerId: string | undefined) {
  const now = new Date();
  const month = now.getMonth() + 1; // 1-12
  const year = now.getFullYear();

  return useQuery({
    queryKey: corporateCustomerId ? QUERY_KEYS.currentBudget(corporateCustomerId, month, year) : ["budget", "null"],
    queryFn: () => (corporateCustomerId ? firestore.getCurrentBudget(corporateCustomerId, month, year) : null),
    enabled: !!corporateCustomerId,
  });
}

export function useCreateBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { corporateCustomerId: string; month: number; year: number; limit: number; alertThreshold?: number; notes?: string }) =>
      firestore.createBudget(data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentBudget(variables.corporateCustomerId, variables.month, variables.year) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.corporateBudgets(variables.corporateCustomerId) });
    },
  });
}

export function useUpdateBudgetSpent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ budgetId, amount }: { budgetId: string; amount: number }) =>
      firestore.updateBudgetSpent(budgetId, amount),
    onSuccess: async (result, variables) => {
      const budget = await firestore.getBudget(variables.budgetId);
      if (budget) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentBudget(budget.corporateCustomerId, budget.month, budget.year) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.corporateBudgets(budget.corporateCustomerId) });
      }
    },
  });
}

// ============================================================================
// SUBSCRIPTION ANALYTICS HOOKS
// ============================================================================

export function useSubscriptionAnalytics() {
  return useQuery({
    queryKey: QUERY_KEYS.subscriptionAnalytics(),
    queryFn: firestore.getSubscriptionAnalytics,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWastedQuotas() {
  return useQuery({
    queryKey: QUERY_KEYS.wastedQuotas(),
    queryFn: firestore.getWastedQuotas,
    staleTime: 5 * 60 * 1000,
  });
}

// Subscription mutations
export function useUpdateSubscriptionQuotas() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, quotas }: { subscriptionId: string; quotas: any }) =>
      firestore.updateSubscriptionQuotas(subscriptionId, quotas),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
    },
  });
}

export function usePauseSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: firestore.pauseCustomerSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
    },
  });
}

export function useResumeSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: firestore.resumeCustomerSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: firestore.cancelCustomerSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allCustomerSubscriptions() });
    },
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users", "all"],
    queryFn: () => firestore.getAllUsers(),
    staleTime: 3 * 60 * 1000,
  });
}
